import { createSlice, PayloadAction, createSelector, createAsyncThunk } from "@reduxjs/toolkit";
import { Org } from "../schema/types/org/entity";
import { Gvc } from "../schema/types/gvc/entity";
import { BillingAccountSummary } from "../schema/types/account";
import { RootState } from ".";
import { request } from "../services/cpln";

export interface OrgInvite {
  id: string;
  context?: {
    orgLink?: string;
  };
}

interface UserDataState {
  orgs: Org[];
  isLoadingOrgs: boolean;

  gvcs: Gvc[];
  isLoadingGvcs: boolean;

  hasWorkload: boolean;
  isLoadingHasWorkload: boolean;

  accountSummaries: BillingAccountSummary[];
  isLoadingAccountSummaries: boolean;

  orgAccountSummaryMap: Record<string, BillingAccountSummary>;

  orgInvites: OrgInvite[];
  isLoadingOrgInvites: boolean;
}

const initialState: UserDataState = {
  orgs: [],
  isLoadingOrgs: false,
  gvcs: [],
  isLoadingGvcs: false,
  hasWorkload: true,
  isLoadingHasWorkload: false,
  accountSummaries: [],
  orgAccountSummaryMap: {},
  isLoadingAccountSummaries: false,
  orgInvites: [],
  isLoadingOrgInvites: false,
};

export const fetchAccountSummaries = createAsyncThunk("userData/fetchAccountSummaries", async () => {
  const { data } = await request({ service: "billing-ng", url: `/account`, timeout: 2000 });
  const res: BillingAccountSummary[] = [];
  for (let item of data.items) {
    res.push({
      accountName: item.accountName,
      fullName: item.fullName,
      id: item.id,
      company: item.extraInfo?.company,
      accountId: item.accountId,
      created: item.created,
      lastModified: item.lastModified,
      orgQuota: item.orgQuota,
    });
  }
  return res;
});

export const fetchOrgs = createAsyncThunk("userData/fetchOrgs", async (_, { dispatch }) => {
  let orgs: Org[] = [];
  let nextLink = `/org`;
  while (nextLink) {
    const { data } = await request({ url: nextLink });
    nextLink = data.links.find((link: any) => link.rel === "next")?.href;
    orgs = orgs.concat(data.items);
  }

  dispatch(fetchAccountsOfOrgs({ orgs: orgs }));

  return orgs;
});

export const fetchAdminOrg = createAsyncThunk("userData/fetchAdminOrg", async (_, { getState, dispatch }) => {
  const state = getState() as RootState;
  const { data } = await request<Org>({ url: `/org/${state.user.org}` });
  return data;
});

export const fetchGvcs = createAsyncThunk("userData/fetchGvcs", async (_, { getState }) => {
  const state = getState() as RootState;
  if (!state.context.org) {
    return [];
  }

  let gvcs: Gvc[] = [];
  let nextLink = `/org/${state.context.org}/gvc`;
  while (nextLink) {
    const { data } = await request({ url: nextLink });
    nextLink = data.links.find((link: any) => link.rel === "next")?.href;
    gvcs = gvcs.concat(data.items);
  }
  return gvcs;
});

export const fetchOrgInvites = createAsyncThunk("userData/fetchOrgInvites", async () => {
  let invites: OrgInvite[] = [];
  let nextLink = "/task/-forme";
  while (nextLink) {
    const { data } = await request({ url: nextLink });
    nextLink = data.links.find((link: any) => link.rel === "next")?.href;
    invites = invites.concat(data.items);
  }
  return invites;
});

// ignore and return true when there is no context or fails, just a best effort to show helper pages
export const fetchHasWorkload = createAsyncThunk("userData/fetchHasWorkload", async (_, { getState }) => {
  const state = getState() as RootState;
  if (!state.context.org) {
    return true;
  }
  if (!state.context.gvc) {
    return true;
  }

  try {
    let url = `/org/${state.context.org}/gvc/${state.context.gvc}/workload`;
    const { data } = await request({ url });
    return data.items.length > 0;
  } catch (e) {
    return true;
  }
});

export const fetchAccountsOfOrgs = createAsyncThunk(
  "userData/fetchAccountsOfOrgs",
  async ({ orgs }: { orgs: Org[] }) => {
    const map: Record<string, BillingAccountSummary> = {};
    const promises: Promise<any>[] = [];
    for (const org of orgs) {
      promises.push(request({ service: "billing-ng", url: `/org/${org.name}/account` }));
    }
    const responses = await Promise.allSettled(promises);
    for (const responseIndex in responses) {
      const response = responses[responseIndex];
      const org = orgs[responseIndex];
      if (response.status === "fulfilled") {
        const accountSummary: BillingAccountSummary = {
          id: response.value.data.id,
          fullName: response.value.data.fullName,
          accountName: response.value.data.accountName,
          company: response.value.data.extraInfo?.company,
        };
        map[org.name] = accountSummary;
      }
    }
    return map;
  },
);

export const userDataSlice = createSlice({
  name: "userData",
  initialState,
  reducers: {
    setHasWorkload(state, action: PayloadAction<boolean>) {
      state.hasWorkload = action.payload;
    },
    updateOrg(state, action: PayloadAction<Org>) {
      const index = state.orgs.findIndex((o) => o.name === action.payload.name);
      if (index !== -1) {
        state.orgs[index] = action.payload;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // Handle fetch account summaries states
      .addCase(fetchAccountSummaries.pending, (state) => {
        state.accountSummaries = [];
        state.isLoadingAccountSummaries = true;
      })
      .addCase(fetchAccountSummaries.fulfilled, (state, action) => {
        state.accountSummaries = action.payload;
        state.isLoadingAccountSummaries = false;
      })
      .addCase(fetchAccountSummaries.rejected, (state) => {
        state.accountSummaries = [];
        state.isLoadingAccountSummaries = false;
      })
      // Handle fetch orgs states
      .addCase(fetchOrgs.pending, (state) => {
        state.orgs = [];
        state.isLoadingOrgs = true;
      })
      .addCase(fetchOrgs.fulfilled, (state, action) => {
        state.orgs = action.payload;
        state.isLoadingOrgs = false;
      })
      .addCase(fetchOrgs.rejected, (state) => {
        state.orgs = [];
        state.isLoadingOrgs = false;
      })
      // Handle fetch admin org states
      .addCase(fetchAdminOrg.pending, (state) => {
        state.orgs = [];
        state.isLoadingOrgs = true;
      })
      .addCase(fetchAdminOrg.fulfilled, (state, action) => {
        state.orgs = [action.payload];
        state.isLoadingOrgs = false;
      })
      .addCase(fetchAdminOrg.rejected, (state) => {
        state.orgs = [];
        state.isLoadingOrgs = false;
      })
      // Handle fetch gvcs states
      .addCase(fetchGvcs.pending, (state) => {
        state.isLoadingGvcs = true;
      })
      .addCase(fetchGvcs.fulfilled, (state, action) => {
        state.gvcs = action.payload;
        state.isLoadingGvcs = false;
      })
      .addCase(fetchGvcs.rejected, (state) => {
        state.gvcs = [];
        state.isLoadingGvcs = false;
      })
      // Handle fetch org invites states
      .addCase(fetchOrgInvites.pending, (state) => {
        state.orgInvites = [];
        state.isLoadingOrgInvites = true;
      })
      .addCase(fetchOrgInvites.fulfilled, (state, action) => {
        state.orgInvites = action.payload;
        state.isLoadingOrgInvites = false;
      })
      .addCase(fetchOrgInvites.rejected, (state) => {
        state.orgInvites = [];
        state.isLoadingOrgInvites = false;
      })
      // Handle fetch has workload states
      .addCase(fetchHasWorkload.pending, (state) => {
        state.hasWorkload = true;
        state.isLoadingHasWorkload = true;
      })
      .addCase(fetchHasWorkload.fulfilled, (state, action) => {
        state.hasWorkload = action.payload;
        state.isLoadingHasWorkload = false;
      })
      .addCase(fetchHasWorkload.rejected, (state) => {
        state.hasWorkload = true;
        state.isLoadingHasWorkload = false;
      })
      // Handle fetch accounts of orgs states
      .addCase(fetchAccountsOfOrgs.pending, (state) => {
        state.orgAccountSummaryMap = {};
      })
      .addCase(fetchAccountsOfOrgs.fulfilled, (state, action) => {
        state.orgAccountSummaryMap = action.payload;
      })
      .addCase(fetchAccountsOfOrgs.rejected, (state) => {
        state.orgAccountSummaryMap = {};
      });
  },
});

export const { setHasWorkload, updateOrg } = userDataSlice.actions;

export const selectOrgs = (state: RootState) => state.userData.orgs;
export const selectGvcs = (state: RootState) => state.userData.gvcs;
export const selectAccountSummaries = (state: RootState) => state.userData.accountSummaries;
export const selectOrgInvites = (state: RootState) => state.userData.orgInvites;

export const selectOrgNames = createSelector([selectOrgs], (orgs) => {
  return orgs.map((org) => org.name);
});

export const selectGvcNames = createSelector([selectGvcs], (gvcs) => {
  return gvcs.map((gvc) => gvc.name);
});

export const selectHasOrg = createSelector([selectOrgs], (orgs) => {
  return orgs.length > 0;
});

export const selectHasGvc = createSelector([selectGvcs], (gvcs) => {
  return gvcs.length > 0;
});

export const selectHasAccount = createSelector([selectAccountSummaries], (accountSummaries) => {
  return accountSummaries.length > 0;
});

export const selectHasOrgInvite = createSelector([selectOrgInvites], (orgInvites) => {
  return orgInvites.length > 0;
});

export const userDataReducer = userDataSlice.reducer;
